import { forwardRef, useId, type InputHTMLAttributes, type TextareaHTMLAttributes, type ReactNode } from 'react';
import { CircleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

// Note: ref is typed as the input|textarea union. A caller using an imperative
// ref together with multiline gets a textarea ref the union won't narrow — an
// accepted limitation (a discriminated union would burden the common call site).
// Re-authored from ../design-system/components/forms/Input.jsx.
interface BaseProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  leadingIcon?: ReactNode;
  multiline?: boolean;
  className?: string;
}

/**
 * Input — composite field (label + control + message). NOTE: `className` is
 * applied to the outer field wrapper (for layout: width, margin), NOT the
 * <input>/<textarea> control — unlike the other ui/ primitives where className
 * styles the element itself. Pass control-level overrides via standard input
 * props, or restyle the wrapper here.
 */
export type InputProps = BaseProps &
  Omit<InputHTMLAttributes<HTMLInputElement> & TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>;

const controlBase =
  'w-full font-sans text-base text-foreground bg-input border border-border rounded-[var(--radius-control)] transition ' +
  'placeholder:text-[var(--ollio-slate-light)] ' +
  '[&:enabled:hover:not(:focus)]:border-[var(--ollio-slate)] ' +
  'focus:outline-none focus:bg-card focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)] ' +
  'disabled:opacity-55 disabled:cursor-not-allowed disabled:bg-[var(--ollio-cream-200)] ' +
  'data-[invalid=true]:border-destructive data-[invalid=true]:focus:ring-[var(--ollio-danger-bg)]';

export const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(function Input(
  { label, hint, error, required = false, leadingIcon, multiline = false, id, className, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;
  const hasIcon = !!leadingIcon && !multiline;
  const sharedClass = cn(
    controlBase,
    multiline ? 'min-h-[88px] px-4 py-3 leading-normal resize-y' : 'h-10 px-4',
    hasIcon && 'pl-10',
  );

  return (
    <div className={cn('flex flex-col gap-2 font-sans', className)}>
      {label && (
        <label htmlFor={fieldId} className="text-sm font-semibold text-[var(--text-heading)]">
          {label}
          {required && <span className="ml-0.5 text-[var(--ollio-gold-deep)]">*</span>}
        </label>
      )}
      <div className="relative flex items-center">
        {hasIcon && (
          <span className="pointer-events-none absolute left-3 flex text-muted-foreground [&_svg]:h-[18px] [&_svg]:w-[18px]">
            {leadingIcon}
          </span>
        )}
        {multiline ? (
          <textarea
            id={fieldId}
            ref={ref as React.Ref<HTMLTextAreaElement>}
            data-invalid={error ? 'true' : undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={sharedClass}
            {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
          />
        ) : (
          <input
            id={fieldId}
            ref={ref as React.Ref<HTMLInputElement>}
            data-invalid={error ? 'true' : undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={sharedClass}
            {...(rest as InputHTMLAttributes<HTMLInputElement>)}
          />
        )}
      </div>
      {error ? (
        <span id={errorId} role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
          <CircleAlert className="h-[13px] w-[13px]" aria-hidden="true" />
          {error}
        </span>
      ) : (
        hint && <span id={hintId} className="text-xs text-muted">{hint}</span>
      )}
    </div>
  );
});
